import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import { MARKET_GLOBAL } from '../../../../../features/app/constants';
import { getIsLocalUser } from '../../../../../features/loginSystem/selectors';
import {
  getGlobalPublishedPpisByItemCode,
  getIsFetchingGlobal
} from '../../../../../features/PpiContainer/selectors';
import {
  getGlobalPublishedPpiIfs,
  getPpsiByEnrichmentId
} from '../../../../../features/PpsiContainer/selectors';
import {
  ppiCreateFromExistingSelectPpiRoute,
  ppiCreateRoute,
  ppiCreateFromPublishedPpiRoute
} from '../../../../../routes';
// import Button from '../../../../common/Button';
import ButtonGroup from '../../../../common/ButtonGroup';
import FormContext from '../../../../Form/FormContext';
import ListGlobalPpis from './ListGlobalPpis';
import Progress from './Progress';

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(1)
  },
  createButton: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(2)
  },
  radioGroupRoot: {
    display: 'block'
  },
  radioButtonRoot: {
    '& .MuiSvgIcon-root': {
      fontSize: '1.2rem'
    }
  },
  ppiLabelContainer: {
    userSelect: 'none'
  }
}));

// (3) User is local
//        (a) Ingredient has PPI(s) created by IFS (i.e. global ingredient)
//            --> List PPI(s) along with copy and show "Create Instruction" (from scratch) button
//        (b) User is in use case "Create from existing PPSI"
//            --> No instructions exists, "Create Instruction" and "Copy Instruction" buttons
//        (c) Otherwise
//            --> No instructions exists, "Create Instruction" button.
const LocalPpiSection = ({
  itemCode,
  globalIngredientId,
  ingredientOwningOrganization,
  onChangeSelectedPpi,
  isExcluded,
  openPreview
}) => {
  const classes = useStyles();
  const isLocalUser = useSelector(getIsLocalUser);
  const globalPpis = useSelector(
    getGlobalPublishedPpisByItemCode(globalIngredientId)
  ).toJS();
  // const globalPpis = useSelector(getGlobalPublishedPpiIfs).toJS();
  const isFetchingGlobal = useSelector(getIsFetchingGlobal);
  const { data } = useContext(FormContext);
  const { parentPpsiEnrichmentDataId } = data;
  const parentPpsi = useSelector(
    getPpsiByEnrichmentId(parentPpsiEnrichmentDataId)
  );
  const [selected, setSelected] = useState(isExcluded);

  const localPpiCopy = 'true';
  const globalPpiCopy = 'false';

  const parentPpsiId = parentPpsi ? parentPpsi.get('id') : null;
  const hasGlobalPpis = globalPpis.length > 0;

  if (!isLocalUser) {
    return null;
  }

  if (isFetchingGlobal) {
    return (
      <Progress>
        <FormattedMessage id="ppi.loadingGlobalInstructions" />
      </Progress>
    );
  }

  const buttonMenuItems = [];

  const dataToPass = isExcluded === undefined ? false : isExcluded;

  function handleExcludeChange(event) {
    setSelected(!selected);
    onChangeSelectedPpi(event, { selected });
  }

  buttonMenuItems.push({
    text: <FormattedMessage id="ppi.copyLocalInstruction" />,
    route: ppiCreateFromPublishedPpiRoute(itemCode, localPpiCopy)
  });

  let content;
  if (hasGlobalPpis) {
    // Scenario (3a)
    content = (
      <>
        <ListGlobalPpis
          globalPpis={globalPpis}
          openPreview={openPreview}
          itemCode={itemCode}
          globalIngredientId={globalIngredientId}
        />
        <ButtonGroup
          route={ppiCreateRoute(itemCode)}
          menuItems={buttonMenuItems}
          classes={{
            root: classes.createButton
          }}
        >
          <FormattedMessage id="ppi.createInstruction" />
        </ButtonGroup>
        <div>
          <FormControlLabel
            value={dataToPass}
            control={
              <Radio
                classes={{ root: classes.radioButtonRoot }}
                onClick={event => handleExcludeChange(event)}
                checked={dataToPass}
              />
            }
            label={
              <div className={classes.ppiLabelContainer}>
                <Typography variant="body2">Exclude</Typography>
              </div>
            }
          />
        </div>
      </>
    );
  } else {
    // Scenario (3b) + (3c)
    const message =
      ingredientOwningOrganization === MARKET_GLOBAL ? (
        <FormattedMessage id="ppi.noGlobalInstructionsExists" />
      ) : (
        <FormattedMessage id="ppsi.noInstructionsExists" />
      );

    // const buttonMenuItems = [];
    if (parentPpsiId) {
      buttonMenuItems.push({
        text: <FormattedMessage id="ppi.copyGlobalInstruction" />,
        route: ppiCreateFromExistingSelectPpiRoute(
          parentPpsiId,
          itemCode,
          globalPpiCopy
        )
      });
    }

    return (
      <div className={classes.container}>
        <Typography variant="body2">{message}</Typography>
        <ButtonGroup
          route={ppiCreateRoute(itemCode)}
          menuItems={buttonMenuItems}
          classes={{
            root: classes.createButton
          }}
        >
          <FormattedMessage id="ppi.createInstruction" />
        </ButtonGroup>
        <div>
          <FormControlLabel
            value={dataToPass}
            control={
              <Radio
                classes={{ root: classes.radioButtonRoot }}
                onClick={event => handleExcludeChange(event)}
                checked={dataToPass}
              />
            }
            label={
              <div className={classes.ppiLabelContainer}>
                <Typography variant="body2">Exclude</Typography>
              </div>
            }
          />
        </div>
      </div>
    );
  }

  return content;
};

export default LocalPpiSection;
